import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowUpRight,
  DollarSign,
  MapPin,
  CalendarCheck,
  AlertTriangle,
  Users,
  Star,
  Activity,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Spayzo" },
      { name: "description", content: "Platform-wide overview for Spayzo admins." },
    ],
  }),
  component: AdminDashboard,
});

const revenueData = [
  { d: "Jan", v: 182000 },
  { d: "Feb", v: 201000 },
  { d: "Mar", v: 224000 },
  { d: "Apr", v: 238000 },
  { d: "May", v: 260000 },
  { d: "Jun", v: 284920 },
];

const bookingsData = [
  { d: "Mon", v: 142 },
  { d: "Tue", v: 168 },
  { d: "Wed", v: 155 },
  { d: "Thu", v: 189 },
  { d: "Fri", v: 214 },
  { d: "Sat", v: 198 },
  { d: "Sun", v: 135 },
];

type KPI = {
  labelKey: string;
  value: React.ReactNode;
  delta: string;
  trend: "up" | "warn";
  hintKey: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  iconBg: string;
  iconColor: string;
};

const kpis: KPI[] = [
  {
    labelKey: "adminIndex.kpis.totalRevenue.label",
    value: <Money value="284,920" />,
    delta: "↑ 14.2%",
    trend: "up",
    hintKey: "adminIndex.kpis.totalRevenue.hint",
    icon: DollarSign,
    accent: "from-[#5958F3]/10 via-transparent to-transparent",
    iconBg: "bg-[#5958F3]/12",
    iconColor: "text-[#5958F3]",
  },
  {
    labelKey: "adminIndex.kpis.activeSpaces.label",
    value: "1,248",
    delta: "↑ 8.4%",
    trend: "up",
    hintKey: "adminIndex.kpis.activeSpaces.hint",
    icon: MapPin,
    accent: "from-emerald-400/10 via-transparent to-transparent",
    iconBg: "bg-emerald-500/12",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  {
    labelKey: "adminIndex.kpis.totalBookings.label",
    value: "9,841",
    delta: "↑ 22.1%",
    trend: "up",
    hintKey: "adminIndex.kpis.totalBookings.hint",
    icon: CalendarCheck,
    accent: "from-sky-400/10 via-transparent to-transparent",
    iconBg: "bg-sky-500/12",
    iconColor: "text-sky-600 dark:text-sky-400",
  },
  {
    labelKey: "adminIndex.kpis.pendingApprovals.label",
    value: "7",
    delta: "adminIndex.kpis.pendingApprovals.delta",
    trend: "warn",
    hintKey: "adminIndex.kpis.pendingApprovals.hint",
    icon: AlertTriangle,
    accent: "from-amber-400/10 via-transparent to-transparent",
    iconBg: "bg-amber-500/12",
    iconColor: "text-amber-600 dark:text-amber-500",
  },
];

const CHART = {
  primary: "#5958F3",
  primarySoft: "#8B8AF7",
  emerald: "#10B981",
  sky: "#0EA5E9",
  amber: "#F59E0B",
  grid: "rgba(148,163,184,0.2)",
};

const chartTooltipStyle = {
  background: "hsl(var(--popover, 0 0% 100%))",
  border: "1px solid rgba(148,163,184,0.25)",
  borderRadius: 12,
  boxShadow: "0 10px 30px -12px rgba(15,23,42,0.18)",
  fontSize: 12,
  padding: "8px 10px",
} as const;

const approvals = [
  { id: "1", name: "Studio Bloom", status: "Pending", metaKey: "adminIndex.approvals.meta.newPartner2h" },
  { id: "2", name: "Zenith Cowork", status: "In Review", metaKey: "adminIndex.approvals.meta.reReview5h" },
  { id: "3", name: "Nomad Hub", status: "Pending", metaKey: "adminIndex.approvals.meta.newPartner1d" },
  { id: "4", name: "Rami's Lounge", status: "In Review", metaKey: "adminIndex.approvals.meta.updatedDocs1d" },
];

const bookings = [
  { user: "Sofia R.", email: "sofia@email.com", space: "Rami's Lounge", date: "Jun 12", amount: <Money value="120" />, status: "Confirmed" },
  { user: "Amir K.", email: "amir@email.com", space: "Zenith Cowork", date: "Jun 11", amount: <Money value="85" />, status: "Confirmed" },
  { user: "Yuki T.", email: "yuki@email.com", space: "Studio Bloom", date: "Jun 10", amount: <Money value="220" />, status: "Pending" },
  { user: "Luca M.", email: "luca@email.com", space: "Nomad Hub", date: "Jun 9", amount: <Money value="65" />, status: "Processing" },
  { user: "Priya S.", email: "priya@email.com", space: "Rami's Lounge", date: "Jun 9", amount: <Money value="150" />, status: "Confirmed" },
];

const activity = [
  { textKey: "adminIndex.activity.items.approved", whenKey: "adminIndex.activity.when.min2", icon: CheckCircle2, tone: "text-emerald-600" },
  { textKey: "adminIndex.activity.items.newBooking", whenKey: "adminIndex.activity.when.min18", icon: CalendarCheck, tone: "text-primary" },
  { textKey: "adminIndex.activity.items.flagged", whenKey: "adminIndex.activity.when.hour1", icon: AlertTriangle, tone: "text-amber-600" },
  { textKey: "adminIndex.activity.items.upgraded", whenKey: "adminIndex.activity.when.hours3", icon: ArrowUpRight, tone: "text-primary" },
  { textKey: "adminIndex.activity.items.cancelled", whenKey: "adminIndex.activity.when.hours5", icon: Clock, tone: "text-muted-foreground" },
  { textKey: "adminIndex.activity.items.payout", whenKey: "adminIndex.activity.when.yesterday430", icon: DollarSign, tone: "text-emerald-600" },
  { textKey: "adminIndex.activity.items.newMerchant", whenKey: "adminIndex.activity.when.yesterday215", icon: Users, tone: "text-primary" },
];

const platformStats = [
  { labelKey: "adminIndex.platformStats.registeredUsers", value: "28,412", icon: Users, bg: "bg-[#5958F3]/12", color: "text-[#5958F3]" },
  { labelKey: "adminIndex.platformStats.activeMerchants", value: "342", icon: MapPin, bg: "bg-emerald-500/12", color: "text-emerald-600 dark:text-emerald-400" },
  { labelKey: "adminIndex.platformStats.avgRating", value: "4.82", icon: Star, bg: "bg-amber-500/12", color: "text-amber-600 dark:text-amber-500" },
  { labelKey: "adminIndex.platformStats.platformUptime", value: "99.8%", icon: Activity, bg: "bg-sky-500/12", color: "text-sky-600 dark:text-sky-400" },
];

function useStatusLabel() {
  const { t } = useTranslation();
  return (s: string) => {
    switch (s) {
      case "Confirmed":
        return t("adminIndex.status.confirmed");
      case "Pending":
        return t("adminIndex.status.pending");
      case "In Review":
        return t("adminIndex.status.inReview");
      case "Processing":
        return t("adminIndex.status.processing");
      default:
        return s;
    }
  };
}

function statusTone(s: string) {
  switch (s) {
    case "Confirmed":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400";
    case "Pending":
      return "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400";
    case "In Review":
      return "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400";
    case "Processing":
      return "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400";
    default:
      return "bg-muted text-foreground";
  }
}

function AdminDashboard() {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  return (
    <AdminShell
      title={t("adminIndex.title")}
      subtitle={t("adminIndex.subtitle")}
    >
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.labelKey}
            className={cn(
              "relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-lg",
            )}
          >
            <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", k.accent)} />
            <div className="relative flex items-center justify-between">
              <span className={cn("grid h-11 w-11 place-items-center rounded-xl", k.iconBg, k.iconColor)}>
                <k.icon className="h-5 w-5" />
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  k.trend === "up"
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
                )}
              >
                {k.trend === "warn" ? t(k.delta) : k.delta}
              </span>
            </div>
            <p className="relative mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(k.labelKey)}
            </p>
            <p className="relative mt-1 font-display text-2xl font-bold tracking-tight">{k.value}</p>
            <p className="relative mt-1 text-xs text-muted-foreground">{t(k.hintKey)}</p>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("adminIndex.charts.monthlyRevenue.title")}</p>
              <p className="text-xs text-muted-foreground">{t("adminIndex.charts.monthlyRevenue.range")}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: CHART.primary }} />
                {t("adminIndex.charts.revenue")}
              </span>
              <Badge variant="secondary" className="rounded-full">{t("adminIndex.charts.monthly")}</Badge>
            </div>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.primary} stopOpacity={0.45} />
                    <stop offset="60%" stopColor={CHART.primary} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={CHART.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="d" tick={{ fontSize: 11, fill: "hsl(215 16% 47%)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(215 16% 47%)" }} tickLine={false} axisLine={false} width={40} />
                <RTooltip
                  contentStyle={chartTooltipStyle}
                  cursor={{ stroke: CHART.primary, strokeOpacity: 0.15, strokeWidth: 2 }}
                  formatter={(v: number) => [`SAR ${v.toLocaleString()}`, t("adminIndex.charts.revenue")]}
                />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={CHART.primary}
                  strokeWidth={2.5}
                  fill="url(#rev)"
                  activeDot={{ r: 5, fill: CHART.primary, stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-display text-xl font-bold text-foreground"><Riyal /> 284,920</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
              ↑ 14.2%
            </span>
            <span>{t("adminIndex.charts.vsLastMonth")}</span>
          </div>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("adminIndex.charts.bookingsTrend.title")}</p>
              <p className="text-xs text-muted-foreground">{t("adminIndex.charts.bookingsTrend.range")}</p>
            </div>
            <Badge variant="secondary" className="rounded-full">{t("adminIndex.charts.weekly")}</Badge>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookingsData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="bars" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.primary} stopOpacity={1} />
                    <stop offset="100%" stopColor={CHART.primarySoft} stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="d" tick={{ fontSize: 11, fill: "hsl(215 16% 47%)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(215 16% 47%)" }} tickLine={false} axisLine={false} width={30} />
                <RTooltip
                  contentStyle={chartTooltipStyle}
                  cursor={{ fill: CHART.primary, fillOpacity: 0.06 }}
                  formatter={(v: number) => [t("adminIndex.charts.bookingsCount", { count: v }), t("adminIndex.charts.bookingsLabel")]}
                />
                <Bar dataKey="v" fill="url(#bars)" radius={[8, 8, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <div>
              <p className="font-display text-xl font-bold">1,201</p>
              <p className="text-muted-foreground">{t("adminIndex.charts.thisWeek")}</p>
            </div>
            <div className="text-end">
              <p className="font-semibold text-emerald-600">↑ 22.1%</p>
              <p className="text-muted-foreground">{t("adminIndex.charts.lastWeek", { count: 984 })}</p>
            </div>
          </div>
        </Card>
      </div>


      {/* Approvals + Activity */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-5 shadow-soft lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{t("adminIndex.pendingApprovalsCard.title")}</p>
              <p className="text-xs text-muted-foreground">{t("adminIndex.pendingApprovalsCard.subtitle")}</p>
            </div>
            <Button asChild size="sm" variant="ghost" className="rounded-lg">
              <Link to="/admin/approvals">{t("adminIndex.pendingApprovalsCard.reviewAll")}</Link>
            </Button>
          </div>
          <ul className="divide-y divide-border/60">
            {approvals.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-xs font-semibold text-primary">
                  {a.name
                    .split(" ")
                    .map((s) => s[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{a.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{t(a.metaKey)}</p>
                </div>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                    statusTone(a.status),
                  )}
                >
                  {statusLabel(a.status)}
                </span>
                <Button asChild size="sm" variant="outline" className="rounded-lg">
                  <Link to="/admin/approvals/$id" params={{ id: a.id }}>
                    {t("adminIndex.pendingApprovalsCard.review")}
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-semibold">{t("adminIndex.activity.title")}</p>
            <Button size="sm" variant="ghost" className="rounded-lg">{t("adminIndex.activity.seeAll")}</Button>
          </div>
          <ol className="relative space-y-4 ps-2">
            {activity.map((e, i) => (
              <li key={i} className="flex gap-3">
                <span className={cn("mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-muted", e.tone)}>
                  <e.icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm leading-snug">{t(e.textKey)}</p>
                  <p className="text-[11px] text-muted-foreground">{t(e.whenKey)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {/* Recent Bookings */}
      <Card className="mt-6 rounded-2xl border-border/60 shadow-soft">
        <div className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-semibold">{t("adminIndex.recentBookings.title")}</p>
            <p className="text-xs text-muted-foreground">{t("adminIndex.recentBookings.subtitle")}</p>
          </div>
          <Button asChild size="sm" variant="ghost" className="rounded-lg">
            <Link to="/admin/bookings">{t("adminIndex.recentBookings.viewAll")}</Link>
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-border/60 bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-2.5 text-start">{t("adminIndex.recentBookings.columns.user")}</th>
                <th className="px-5 py-2.5 text-start">{t("adminIndex.recentBookings.columns.space")}</th>
                <th className="px-5 py-2.5 text-start">{t("adminIndex.recentBookings.columns.date")}</th>
                <th className="px-5 py-2.5 text-start">{t("adminIndex.recentBookings.columns.amount")}</th>
                <th className="px-5 py-2.5 text-start">{t("adminIndex.recentBookings.columns.status")}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={i} className="border-b border-border/40 last:border-b-0">
                  <td className="px-5 py-3">
                    <p className="font-semibold">{b.user}</p>
                    <p className="text-xs text-muted-foreground">{b.email}</p>
                  </td>
                  <td className="px-5 py-3">{b.space}</td>
                  <td className="px-5 py-3 text-muted-foreground">{b.date}</td>
                  <td className="px-5 py-3 font-semibold">{b.amount}</td>
                  <td className="px-5 py-3">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", statusTone(b.status))}>
                      {statusLabel(b.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Platform stats */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {platformStats.map((s) => (
          <Card key={s.labelKey} className="rounded-2xl border-border/60 p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-lg">
            <span className={cn("grid h-11 w-11 place-items-center rounded-xl", s.bg, s.color)}>
              <s.icon className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-2xl font-bold tracking-tight">{s.value}</p>
            <p className="text-xs text-muted-foreground">{t(s.labelKey)}</p>
          </Card>
        ))}
      </div>
    </AdminShell>
  );
}
